import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface User {
  userId: string;
  email: string;
  roles: string[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (token: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check authentication by calling /api/auth/me on login.weretrade.com
    // This will use the session cookie automatically
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get('https://login.weretrade.com/api/auth/me', {
        withCredentials: true // Send cookies
      });
      
      const data = response.data;
      
      // Check if user has admin role
      if (!data.profile?.role || data.profile.role !== 'admin') {
        throw new Error('Admin access required');
      }

      setUser({
        userId: String(data.user.id),
        email: data.user.username,
        roles: [data.profile.role || 'user'],
      });
    } catch (error) {
      console.error('Authentication check failed:', error);
      // If not authenticated, redirect to login
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        window.location.href = 'https://login.weretrade.com?redirect=' + encodeURIComponent(window.location.href);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (newToken: string) => {
    // Not used in cookie-based auth, but keep for compatibility
    setToken(newToken);
    await checkAuth();
  };

  const logout = async () => {
    try {
      await axios.post('https://login.weretrade.com/api/auth/logout', {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
    setUser(null);
    setToken(null);
    window.location.href = 'https://login.weretrade.com';
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
